import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import {getDefaultFieldLabel} from "../../../../utilities/helperFunctions";
import BaseFieldComponent from "../BaseFieldComponent";
import FilledTextField from "../../../../core/inputs/FilledTextField";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import Text from "../../../../connectui/component/display/Text";
import TextInput from "../../../../connectui/component/input/text/type/TextInput";
import OutlinedInputBase from "../../../../connectui/component/input/text/OutlinedInputBase";
import {useTheme} from "../../../../connectui/theme/ThemeProvider";
import Container from "../../../../connectui/component/layout/Container";

const renderInput = ({fieldData, handleValueChange}) => {
  return (
    <Container sx={{width: "100%", height: "100%"}}>
      <FilledTextField
        fullWidth
        label="Value"
        type="number"
        inputProps={{min: 0, max: 100000}}
        value={fieldData.val}
        onChange={(e) => handleValueChange(fieldData.type, e.target.value)}
      />
    </Container>
  );
};

const RangeFilterComponent = ({fieldData, handleValueChange}) => {
  const {theme} = useTheme();
  const [fromValue, setFromValue] = useState("");
  const [toValue, setToValue] = useState("");

  useEffect(() => {
    const {val, operators} = fieldData;
    if (Array.isArray(val) && Array.isArray(operators)) {
      const greaterThanIndex = operators.findIndex((op) => op === ">=");
      const lessThanIndex = operators.findIndex((op) => op === "<=");

      if (greaterThanIndex !== -1) {
        setFromValue(val[greaterThanIndex]?.toString() || "");
      }
      if (lessThanIndex !== -1) {
        setToValue(val[lessThanIndex]?.toString() || "");
      }
    }
  }, [fieldData.val, fieldData.operators]);

  useEffect(() => {
    if (fromValue !== "" || toValue !== "") {
      const filters = [];
      const from = parseFloat(fromValue);
      const to = parseFloat(toValue);

      if (!isNaN(from)) {
        filters.push({path: fieldData.field, operator: ">=", value: from});
      }
      if (!isNaN(to)) {
        filters.push({path: fieldData.field, operator: "<=", value: to});
      }
      handleValueChange("custom", filters);
    }
  }, [fromValue, toValue]);

  const renderInput = (value, onChange, placeholder) => (
    <FlexContainer alignItems="center" gap={theme.spacing(1)} sx={{flex: 1}}>
      <Text variant="textSM" weight="medium" color="textSecondary">
        $
      </Text>
      <TextInput
        inputBase={OutlinedInputBase}
        inputProps={{
          type: "number",
          min: 0,
          max: 1000000,
          value,
          onChange,
          placeholder,
        }}
        sx={{
          flex: 1,
          padding: theme.spacing(1, 2),
        }}
      />
    </FlexContainer>
  );

  return (
    <FlexContainer
      flexDirection="column"
      gap={theme.spacing(3)}
      sx={{width: "100%"}}
    >
      <FlexContainer alignItems="center" gap={theme.spacing(2)}>
        {renderInput(fromValue, (e) => setFromValue(e.target.value), "Min")}
        <Text
          variant="textSM"
          weight="medium"
          color="textTertiary"
          sx={{minWidth: "fit-content", padding: theme.spacing(0, 1)}}
        >
          —
        </Text>
        {renderInput(toValue, (e) => setToValue(e.target.value), "Max")}
      </FlexContainer>

      {(fromValue !== "" || toValue !== "") && (
        <FlexContainer
          justifyContent="center"
          sx={{
            backgroundColor: theme.colors.primary50,
            borderRadius: theme.spacing(1),
            padding: theme.spacing(1.5, 3),
          }}
        >
          <Text variant="textSM" weight="semibold" color="primary">
            {(() => {
              const from = parseFloat(fromValue);
              const to = parseFloat(toValue);

              if (!isNaN(from) && !isNaN(to)) {
                return `$${from.toLocaleString()} - $${to.toLocaleString()}`;
              } else if (!isNaN(from)) {
                return `≥ $${from.toLocaleString()}`;
              } else if (!isNaN(to)) {
                return `≤ $${to.toLocaleString()}`;
              }
              return "";
            })()}
          </Text>
        </FlexContainer>
      )}
    </FlexContainer>
  );
};

const renderFilter = ({fieldData, handleValueChange}) => {
  return (
    <RangeFilterComponent
      fieldData={fieldData}
      handleValueChange={handleValueChange}
    />
  );
};

const getLabel = (fieldData) => {
  const {val, operators} = fieldData;
  if (Array.isArray(val) && Array.isArray(operators)) {
    const greaterThanIndex = operators.findIndex((op) => op === ">=");
    const lessThanIndex = operators.findIndex((op) => op === "<=");

    const from = greaterThanIndex !== -1 ? val[greaterThanIndex] : null;
    const to = lessThanIndex !== -1 ? val[lessThanIndex] : null;

    if (from !== null && to !== null) {
      return `$${from} - $${to}`;
    } else if (from !== null) {
      return `≥ $${from}`;
    } else if (to !== null) {
      return `≤ $${to}`;
    }
  }
  return getDefaultFieldLabel(fieldData?.val);
};

const getOperators = () => [
  "is",
  "is_not",
  "is_greater_than",
  "is_less_than",
  "exists",
  "not_exists",
];

const IntegerRangeField = ({
  fieldData,
  handleValueChange,
  options,
  ...props
}) => {
  return (
    <BaseFieldComponent
      renderInput={() => renderInput({fieldData, handleValueChange})}
      renderFilter={() => renderFilter({fieldData, handleValueChange})}
      getLabel={() => getLabel(fieldData)}
      getOperators={getOperators}
      {...props}
    />
  );
};

IntegerRangeField.propTypes = {
  fieldData: PropTypes.object.isRequired,
  handleValueChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
};

export default IntegerRangeField;
